<template>
  <div class="grid grid-rows-[auto_1fr] h-full page-mate-create">
    <threads-nav
      :show-back="true"
      :show-center="!userStore.isLogin"
      class="min-h-[56px]"
    >
      <template #left>
        <template v-if="isEdit">
          <button
            v-if="!editingSprite?.isDefault"
            class="btn btn-xs rounded btn-outline"
            @click="setDefault"
          >
            {{ $t("Buttons.setDefault") }}
          </button>
          <div
            v-else
            class="flex items-center gap-1 text-xs text-base-content/50"
          >
            <i class="iconify mingcute--check-circle-line" />
            {{ $t("Message.default") }}
          </div>
        </template>
      </template>
      <template #center v-if="!userStore.isLogin">
        {{ $t("sprite.create") }}
      </template>

      <template #right>
        <template v-if="userStore.isLogin">
          <button
            v-if="isEdit"
            class="btn btn-xs rounded btn-outline"
            @click="deleteSprite"
          >
            {{ $t("Buttons.delete") }}
          </button>
          <button
            v-if="!isEdit"
            class="btn btn-primary btn-xs"
            :disabled="checkIsGenerating"
            @click="clickCreate()"
          >
            {{ $t("Buttons.create1") }}
          </button>
        </template>

        <button v-else class="btn btn-primary btn-xs" @click="clickLogin">
          {{ $t("Buttons.confirm") }}
        </button>
      </template>
    </threads-nav>

    <div class="flex flex-col flex-1 overflow-auto relative">
      <div v-if="!userStore.isLogin" class="flex items-center pl-8 pr-4 py-2">
        <div class="red-dot"></div>
        <div class="font-semibold">{{ $t("sprite.step.1") }}</div>
        <div class="border-t border-dashed flex-1 mr-5 ml-2"></div>
        <div class="red-dot grayscale"></div>
        <div class="text-base-content/80">{{ $t("sprite.step.2") }}</div>
      </div>
      <div
        v-if="editingSprite?.status === MateSpriteStatus.Processing"
        class="flex flex-col items-center justify-center h-full bg-[rgba(0,0,0,0.5)] absolute top-0 left-0 w-full z-10"
      >
        <div class="loading loading-ring loading-lg"></div>
        <div class="text-center" v-html="$t('sprite.generating')" />
      </div>
      <div class="flex items-center gap-2 px-4" v-if="!isPreset && !isImage">
        <van-field
          v-model="name"
          placeholder="形象名称"
          input-align="center"
          class="rounded-lg flex-1"
        />
        <div class="flex flex-col text-xs gap-0.5 text-base-content/50">
          <div
            class="flex items-center justify-between"
            v-if="mateStore.getMyMateById(editingSprite?.mateId)?.gender"
          >
            <div class="flex-1">
              {{ $t("sprite.currentGender") }}：
              <span>
                {{
                  useGender(
                    mateStore.getMyMateById(editingSprite?.mateId)!.gender!
                  )
                }}
              </span>
            </div>

            <button
              class="btn btn-xs rounded btn-outline"
              @click="
                router.push({
                  name: 'mateEdit',
                  params: {
                    uuid: mateStore.getMyMateById(editingSprite?.mateId)!.uuid,
                  },
                })
              "
            >
              {{ $t("Buttons.change") }}
            </button>
          </div>

          <p class="text-center">
            {{ $t("sprite.nameAndSpriteCanBeChanged") }}
          </p>
        </div>
      </div>
      <div class="grid grid-rows-[1fr_auto] overflow-y-auto h-full">
        <div class="flex flex-col overflow-auto relative">
          <div
            :class="[
              'flex gap-2 overflow-hidden items-center justify-evenly min-h-[152px] h-full flex-1 mate-create-resize-container px-4',
              isAllGenerating ? 'opacity-30 pointer-events-none' : '',
            ]"
          >
            <!-- 立绘 -->
            <div
              class="flex flex-col gap-1 w-fit min-w-[66%] justify-center items-center"
            >
              <div
                v-if="editingSprite?.portraitMedia"
                class="h-full w-full max-w-[280px] relative"
              >
                <div
                  class="absolute top-[50%] right-4 flex flex-col gap-2 -translate-y-1/2"
                  v-if="isEdit"
                >
                  <button
                    class="btn btn-xs px-1 btn-square rounded"
                    :disabled="isFullGenerating || isPortraitGenerating"
                    @click="showDialogueBoxPreviewPopup = true"
                  >
                    <i class="iconify mingcute--eye-line" />
                  </button>
                  <button
                    class="btn btn-xs btn-square rounded"
                    :disabled="isFullGenerating || isPortraitGenerating"
                    @click="clickUploadOrRegen('portrait')"
                  >
                    <i class="iconify mingcute--edit-2-line" />
                  </button>

                  <button
                    class="btn btn-xs btn-square rounded"
                    :disabled="isFullGenerating || isPortraitGenerating"
                    @click="clickDelete('portrait')"
                  >
                    <i class="iconify mingcute--delete-2-fill" />
                  </button>
                </div>
                <img
                  :src="usePreviewImg(editingSprite.portraitMedia.url)"
                  alt=""
                  :class="['object-contain h-full']"
                  crossorigin="anonymous"
                  @click="
                    clickOpenMediaPreviewModal(
                      editingSprite.portraitMedia.url,
                      'portrait'
                    )
                  "
                />
              </div>

              <div
                v-else-if="isImage && (!spriteId || editingSprite.status === 1)"
                class="w-[45%] max-h-full"
              >
                <ImgPortraitPlaceholder :class="['text-secondary']" />
              </div>
              <div v-else>
                <button class="btn" @click="clickUploadOrRegen('portrait')">
                  <i class="iconify mingcute--add-fill w-4" />
                  {{ $t("sprite.portrait") }}
                </button>
              </div>

              <p class="text-xs text-base-content/50 text-center">
                {{ $t("sprite.displayInHouseEvent") }}
              </p>
            </div>

            <div class="flex flex-col gap-1 w-[30%]">
              <!-- 头像 -->
              <div class="flex flex-col gap-1 justify-center items-center">
                <div v-if="editingSprite?.avatarMedia" class="flex-1 relative">
                  <div class="absolute top-0 -right-1" v-if="isEdit">
                    <button
                      class="btn btn-xs px-1"
                      :disabled="isFullGenerating || isAvatarGenerating"
                      @click="clickUploadOrRegen('avatar')"
                    >
                      <i class="iconify mingcute--edit-2-line" />
                    </button>
                  </div>
                  <img
                    :src="usePreviewImg(editingSprite.avatarMedia.url)"
                    alt=""
                    :class="['object-contain w-full']"
                    crossorigin="anonymous"
                    @click="
                      clickOpenMediaPreviewModal(
                        editingSprite.avatarMedia.url,
                        'avatar'
                      )
                    "
                  />
                </div>

                <div v-else-if="isImage" class="flex-1">
                  <ImgAvatarPlaceholder :class="['text-secondary']" />
                </div>
                <div v-else>
                  <button class="btn" @click="clickUploadOrRegen('avatar')">
                    <i class="iconify mingcute--add-fill w-4" />
                    {{ $t("sprite.avatar") }}
                  </button>
                </div>

                <p class="text-xs text-base-content/50 text-center">
                  {{ $t("sprite.displayInHouseDialog") }}
                </p>
              </div>

              <!-- 像素小人 -->
              <div>
                <div
                  v-if="editingSprite?.animationzz?.[0]?.media?.url"
                  class="mr-1 relative"
                  @click="clickFrame()"
                >
                  <div
                    v-if="isEdit"
                    class="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2"
                  >
                    <button
                      class="btn btn-xs px-1"
                      :disabled="isFrameGenerating || isFullGenerating"
                    >
                      <i class="iconify mingcute--edit-2-line" />
                    </button>
                  </div>
                  <div
                    :class="['grid grid-cols-2 w-full gap-x-1.5']"
                    :key="editingSprite?.animationzz[0].media?.url"
                  >
                    <div
                      v-for="animation in editingSprite?.animationzz"
                      :key="animation.media?.id"
                      class="flex items-center justify-center"
                    >
                      <SpriteFrame :url="animation.media?.url!" class="w-12" />
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="isImage || isFrameGenerating"
                  class="flex-1 relative"
                >
                  <ImgFramePlaceholder :class="['text-secondary']" />
                </div>

                <div v-else class="flex justify-center">
                  <button class="btn py-2 h-fit" @click="clickFrame">
                    <i class="iconify mingcute--add-fill w-4" />
                    {{ $t("sprite.frame") }}
                  </button>
                </div>
                <p class="text-xs text-base-content/50 text-center">
                  {{ $t("sprite.displayInHouseFrame") }}
                </p>
              </div>
            </div>
          </div>

          <MateCreateImgLoading
            v-if="editingSprite.status !== MateSpriteStatus.Processing"
            :singlePercent="allCountDownPercent"
            :needWait="false"
            isTask
            :isFrameGenerating="isAllGenerating"
          />
        </div>

        <div
          class="overflow-hidden flex-1 h-[180px] min-h-[180px] flex-shrink-0"
        >
          <van-tabs
            v-model:active="mode"
            v-if="userStore.isLogin && !isPreset && !isImage"
          >
            <van-tab
              :title="$t('sprite.useReferenceImage')"
              :name="MateSpriteMode.Image"
            />
            <van-tab
              :title="$t('sprite.presetSprite')"
              :name="MateSpriteMode.Compose"
            />
          </van-tabs>

          <template v-if="mode === MateSpriteMode.Compose">
            <div class="flex items-center px-2 gap-1 my-1 justify-between">
              <div>
                <button
                  class="btn btn-xs rounded"
                  :class="{ 'btn-primary': !gender }"
                  @click="clickGender()"
                >
                  {{ $t("sprite.all") }}
                </button>

                <button
                  class="btn btn-xs rounded"
                  :class="{ 'btn-primary': gender === 1 }"
                  @click="clickGender(1)"
                >
                  {{ $t("sprite.male") }}
                </button>
                <button
                  class="btn btn-xs rounded"
                  :class="{ 'btn-primary': gender === 2 }"
                  @click="clickGender(2)"
                >
                  {{ $t("sprite.female") }}
                </button>
              </div>
              <button
                class="btn btn-xs"
                @click="showSpriteEditor = true"
                :disabled="checkIsGenerating"
                v-if="!isPreset"
              >
                {{ $t("sprite.custom") }}
              </button>
            </div>
            <div
              class="flex flex-shrink-0 overflow-x-auto px-2 gap-2 pt-1"
              @scroll="handleScroll"
            >
              <div
                v-for="item in spriteList"
                :key="item.id"
                :class="[
                  'bg-base-200 rounded-lg border h-[90px] flex-shrink-0',
                  editingSprite?.id === item.id ? 'border-primary' : '',
                ]"
              >
                <div class="-mt-2 relative" @click="clickPreset(item, true)">
                  <sprite-frame
                    :url="
                      item.animationzz?.find(
                        (v) => v.direction === MateSpriteDirection.Down
                      )?.media?.url ?? ''
                    "
                  />
                </div>
              </div>
              <div
                class="bg-base-200 rounded-lg border h-[90px] w-12 flex-shrink-0 flex items-center justify-center"
                v-if="isLoadingNextPage"
              >
                <div class="loading loading-ring loading-lg"></div>
              </div>
            </div>
          </template>
          <div
            class="flex-1 bg-base-100 px-4 overflow-y-auto h-full"
            v-else-if="mode === MateSpriteMode.Image"
          >
            <!-- Step 1: 选择参考形象 -->
            <div v-if="currentStep === 1" class="h-full flex flex-col">
              <!-- <VanField label="补充描述" placeholder="请输入对于该图片的描述" /> -->
              <div class="flex items-center justify-center pt-4">
                <button
                  @click="clickUpload('reference')"
                  class="btn btn-primary btn-wide"
                >
                  {{ $t("sprite.selectImage") }}
                </button>
              </div>
            </div>

            <!-- Step 2: 生成流程 -->
            <div v-else class="flex flex-col gap-0.5">
              <!-- Reference Image and Generated Sprites -->

              <div class="flex gap-2 w-full items-center">
                <!-- Reference Image -->
                <div>
                  <div
                    class="w-12 aspect-[3/4]"
                    @click="
                      modalStore.openMediaPreviewModal([selectedImage?.url!])
                    "
                  >
                    <UserAvatar :avatar-url="selectedImage?.url" />
                  </div>
                  <button
                    class="btn btn-xs"
                    :disabled="
                      isFrameGenerating || isFullGenerating || isAllGenerating
                    "
                    @click="clickUpload('reference')"
                  >
                    {{ $t("sprite.change") }}
                  </button>
                </div>
                <div class="flex-1 h-full">
                  <button
                    @click="handleGenerate"
                    :disabled="
                      !canProceedToGeneration ||
                      isFrameGenerating ||
                      isFullGenerating ||
                      isAllGenerating
                    "
                    :class="[
                      'btn w-full  h-14 flex items-center',
                      currentStep === 2 ? 'btn-primary' : '',
                    ]"
                  >
                    <div class="flex-1 flex flex-col gap-1">
                      <span>
                        {{
                          currentStep === 2
                            ? $t("sprite.generateNow")
                            : $t("sprite.regenerate")
                        }}
                      </span>
                      <span>
                        {{ $t("sprite.includePortraitAndAvatarAndFrame") }}
                      </span>
                    </div>
                    <div class="flex items-center gap-1">
                      <PowerIcon />
                      8
                    </div>
                  </button>
                </div>
              </div>
              <VanField
                :label="$t('sprite.promptDescription')"
                :placeholder="$t('sprite.promptDescriptionPlaceholder')"
                v-model="promptDescription"
              />
            </div>
          </div>
        </div>
      </div>

      <VanPopup
        v-model:show="showMediaPreviewPopup"
        v-if="editingSprite?.animationzz?.length"
      >
        <VanImagePreview
          :images="editingSprite?.animationzz?.map((v) => v.media?.url!)"
          v-model:show="showMediaPreviewPopup"
          :start-position="activeAnimationIndex"
          class="media-preview"
          closeable
          showIndicators
          teleport="body"
          :close-on-click-image="false"
          @close="showMediaPreviewPopup = false"
        >
          <template #image="{ src }">
            <div class="w-full h-full flex justify-center" :key="src">
              <SpriteFrame class="w-40 bg-transparent" :url="src" />
            </div>
          </template>
        </VanImagePreview>
      </VanPopup>

      <MediaSelectPopup
        v-model:show="showMediaSelectPopup"
        :max-count="1"
        :multiple="false"
        :upload-type="7"
        media-type="img"
        :enable-crop="true"
        :crop-aspect-ratio="3 / 4"
        replace
        :enable-use-avatar="userStore.isLogin"
        @on-media-selected="handleImageSelected"
      />

      <CharacterGeneratorPopup
        v-model:show="showSpriteEditor"
        :sprite="editingSprite"
        :mate-id="mateStore.currentMate?.id"
        @saved="checkSpriteStatusIfIsPending()"
        @single-frame="openSingleFramePopup"
        from="mate-create"
      />

      <CharacterGeneratorCustomPopup
        v-if="mateStore.currentMate?.id"
        v-model:show="showCustomSpriteEditor"
        :sprite="editingSprite"
        :mate-id="mateStore.currentMate.id"
        @saved="
          () => {
            editingSprite.prompt = undefined;
            initEditSprite(true);
          }
        "
        @frame-click="openFramePopup"
      />

      <VanImagePreview
        :images="mediaPreviewUrl ? [mediaPreviewUrl] : []"
        v-model:show="showMediaPreviewModal"
        class="media-preview"
        closeable
        showIndicators
        teleport="body"
        :close-on-click-image="false"
        @close="showMediaPreviewModal = false"
      >
        <template #image="{ src }">
          <div
            ref="previewRef"
            class="max-w-xl my-0 mx-auto flex flex-col justify-center items-center gap-4"
          >
            <img
              class="img-render-nearest min-w-40"
              :src="src"
              :key="src"
              crossorigin="anonymous"
            />
          </div>
        </template>
      </VanImagePreview>
      <DialogueBoxPreviewPopup
        v-if="editingMate"
        v-model:show="showDialogueBoxPreviewPopup"
        :mate="editingMate"
        :portrait="editingSprite.portraitMedia?.url"
      />

      <ModalMateCreateEditAvatarOrPortrait
        v-model:show="showEditAvatarOrPortrait"
        :mate-sprite="editingSprite"
        :type="activeType!"
        @generate="handleEditAvatarOrPortrait"
        @upload="handleUpload"
        @change-reference="clickUpload('reference')"
        @change-pixel="clickFrame()"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  createMateSprite,
  deleteMateSprite,
  readMateSprite,
  readMateSpritePresetPage,
  updateMateSprite,
} from "@/apis/script-game";
import { Sex } from "@/enums/characterGeneratorEnums";
import { MateSpriteDirection, MateSpriteTaskMode } from "@/enums/game";
import { useMate } from "@/stores/mate";
import { type Mate, Media } from "@/types/entity";
import {
  MateSprite,
  MateSpriteAnimation,
  MateSpriteMode,
  MateSpriteStatus,
} from "@/types/game";
import { SelectedItems } from "@/types/sheet-definiation";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { useDungeon } from "@/stores/dungeon";
import { useRoute, useRouter } from "vue-router";
import {
  CustomGlobalActionSheetItem,
  useGlobalModal,
} from "@/stores/globalModal";
import { MediaUploadFileItem } from "@/types/component";
import { useGlobalUser } from "@/stores/globalUser";
import { useI18n } from "vue-i18n";
import { useHouseSession } from "@/features/dream-home/store/houseSession";
import { useGender, usePreviewImg } from "@/use/common";
import api from "@/modules/api";
import { routerBack } from "@/use/go";
import { uploadMediaFromUrl } from "@/components/Mate/Sprite/imgHandler";
import {
  cloneMateSprite,
  handleMateSpriteTask,
  useCreateMateTask,
  useSpriteTask,
} from "./MateCreateTask";
import { useRouteQuery } from "@vueuse/router";
import { MateType } from "@/enums/mateEnums";
import MateCreateImgLoading from "@/components/Mate/Create/MateCreateImgLoading.vue";
import DialogueBoxPreviewPopup from "@/features/dream-home/components/popup/DialogueBoxPreviewPopup.vue";
import { sessionStorageSet } from "@/tools/sessionStorage";

const userStore = useGlobalUser();
const route = useRoute();
const showSpriteEditor = ref(false);
const name = ref("");
const mateStore = useMate();
const dungeonStore = useDungeon();
const router = useRouter();
const { t } = useI18n();
const houseStore = useHouseSession();
const isPreset = useRouteQuery("isPreset", 0, {
  transform: Number,
});

const showEditAvatarOrPortrait = ref(false);
const activeType = ref<"portrait" | "avatar">();

const showMediaPreviewModal = ref(false);
const mediaPreviewType = ref<"portrait" | "avatar" | "reference">("portrait");
const mediaPreviewUrl = ref<string>("");

const editingSprite = ref<MateSprite>({} as MateSprite);
const isImage = useRouteQuery("isImage", 0, {
  transform: Number,
});
const spriteId = useRouteQuery("spriteId", undefined, {
  transform: Number,
});
const createMateType = ref<MateType>();
const isEdit = computed(() => !!spriteId.value);
const showDialogueBoxPreviewPopup = ref(false);
const editingMate = computed(() =>
  editingSprite.value.mateId
    ? mateStore.getMyMateById(editingSprite.value.mateId!)
    : undefined
);
// ----预设形象----
const spriteList = ref<MateSprite[]>([]);
const PER_PAGE = 25;
const gender = ref<number>();

const mode = ref<MateSpriteMode>(MateSpriteMode.Image);
const isLoaded = ref(false);

const loadNextPage = async () => {
  const { length } = spriteList.value;

  if (length !== 0 && length % PER_PAGE !== 0) {
    return false;
  }

  let currentGender = gender.value;

  const page = Math.floor(length / PER_PAGE) + 1;

  const res = await readMateSpritePresetPage({
    filter: {
      isPreset: 1,
      gender: currentGender,
    },
    pagination: {
      page,
      perPage: PER_PAGE,
    },
  });

  if (currentGender !== gender.value) return true;

  if (page === 1 && !route.query.spriteId && !isImage.value) {
    clickPreset(res.page.data[0]);
  }

  spriteList.value = [...spriteList.value, ...res.page.data];
  // return res.page.data.length === PER_PAGE;
  isLoadingNextPage.value = false;
  if (res.page.data.length !== PER_PAGE) {
    isLoaded.value = true;
  }
};

const setDefault = async () => {
  await updateMateSprite(editingSprite.value!.id!, {
    isDefault: true,
  });
  mateStore.fetchMateList(false);
  editingSprite.value!.isDefault = true;
};

const clickGender = (_gender?: number) => {
  if (isLoadingNextPage.value) return;

  isLoadingNextPage.value = true;
  gender.value = _gender;
  nextTick(() => {
    // reload();
    spriteList.value = [];
    isLoaded.value = false;
    loadNextPage();
  });
};

const spriteSex = ref<Sex>(Sex.Male);

const spriteItems = ref<SelectedItems>({} as SelectedItems);

const clickPreset = async (_item: MateSprite, withSave = false) => {
  if (checkIsGenerating.value)
    return showFailToast(t("sprite.pleaseWaitTaskComplete"));

  let item = JSON.parse(JSON.stringify(_item));
  if (mateStore.isMyMateById(editingSprite.value.mateId)) {
    item.uuid = editingSprite.value.uuid;
  }

  if (_item.animationzz?.length && editingSprite.value.animationzz?.length) {
    initImageSprite({
      animations: _item.animationzz,
      imageMedia: _item.imageMedia!,
    });
    item.animationzz = editingSprite.value.animationzz;
  }

  editingSprite.value = item;
  if (item.prompt) initPreviewFromPrompt(item.prompt);

  if (withSave && spriteId.value) {
    try {
      const { avatarMediaId, portraitMediaId, imageMediaId } =
        editingSprite.value || {};
      if (avatarMediaId || portraitMediaId || imageMediaId)
        await showConfirmDialog({
          message: t("Message.confirmToEditPreset"),
          confirmButtonText: t("Buttons.confirm"),
          cancelButtonText: t("Buttons.cancel"),
        });
    } catch {
      return;
    }
    await updateMateSprite(spriteId.value, {
      name: name.value,
      mode: MateSpriteMode.Compose,
      imageMediaId: item.imageMedia!.id,
      avatarMediaId: item.avatarMedia!.id,
      portraitMediaId: item.portraitMedia!.id,
      referenceMediaId: null,
      frameMediaId: item.frameMedia!.id,
      prompt: item.prompt,
    });

    showSuccessToast(t("Message.opSuccess"));
  }
};

const initPreviewFromPrompt = (prompt: string) => {
  const { sex, items } = JSON.parse(prompt);
  spriteSex.value = sex;
  spriteItems.value = items;
  mode.value = MateSpriteMode.Compose;
};

// 参考图生成
const currentStep = ref(1);
const showMediaSelectPopup = ref(false);
const modalStore = useGlobalModal();
const selectedImage = ref<MediaUploadFileItem | null>(null);

const handleImageSelected = async (mediaList: MediaUploadFileItem[]) => {
  const media = mediaList[0];
  if (mediaPreviewType.value === "reference") {
    selectedImage.value = media;
    currentStep.value = 2;

    const loading = showLoadingToast(t("sprite.updatingReferenceImage"));
    try {
      if (isEdit.value) {
        await updateMateSprite(spriteId.value, {
          referenceMediaId: media.mediaId,
        });
        editingSprite.value.referenceMediaId = media.mediaId;
        editingSprite.value.referenceMedia = {
          id: media.mediaId,
          url: media.url,
        } as Media;
      }
      loading.close();

      showMediaSelectPopup.value = false;
    } catch (e) {
      loading.close();
    }
  } else if (mediaPreviewType.value === "avatar") {
    const loading = showLoadingToast(t("sprite.updatingAvatar"));
    try {
      await updateMateSprite(editingSprite.value.id!, {
        avatarMediaId: media.mediaId,
      });
      editingSprite.value.avatarMediaId = media.mediaId;
      editingSprite.value.avatarMedia = {
        id: media.mediaId,
        url: media.url,
      } as Media;
      loading.close();
      showMediaPreviewModal.value = false;
    } catch (e) {
      loading.close();
    }
  } else if (mediaPreviewType.value === "portrait") {
    const loading = showLoadingToast(t("sprite.updatingPortrait"));
    try {
      await updateMateSprite(editingSprite.value.id!, {
        portraitMediaId: media.mediaId,
      });
      editingSprite.value.portraitMediaId = media.mediaId;
      editingSprite.value.portraitMedia = {
        id: media.mediaId,
        url: media.url,
      } as Media;
      loading.close();
      showMediaPreviewModal.value = false;
    } catch (e) {
      loading.close();
    }
  }
};

const handleUpload = () => {
  mediaPreviewType.value = activeType.value!;
  showMediaSelectPopup.value = true;
};

const {
  startCreateMateTask,
  isGenerating: isAllGenerating,
  countDownPercent: allCountDownPercent,
  reset: resetFrameCreate,
} = useCreateMateTask({
  mateSprite: editingSprite,
  mode,
  finish: () => {
    initSort();
    spriteId.value = editingSprite.value.id!;
  },
  selectedImage,
});

const {
  createTask,
  reset: resetFrameTask,
  isGenerating: isFrameGenerating,
} = useSpriteTask({
  mateSprite: editingSprite,
  taskMode: MateSpriteTaskMode.Frame,
  finish: (editingSprite: MateSprite) => {
    initImageSprite({
      animations: editingSprite.animationzz!,
      imageMedia: editingSprite.imageMedia!,
    });
  },
});

const {
  createTask: createAvatarTask,
  reset: resetAvatarTask,
  isGenerating: isAvatarGenerating,
} = useSpriteTask({
  mateSprite: editingSprite,
  taskMode: MateSpriteTaskMode.Avatar,
  finish: (mateSprite: MateSprite) => {
    editingSprite.value.avatarMedia = mateSprite.avatarMedia;
    editingSprite.value.avatarMediaId = mateSprite.avatarMedia?.id;
  },
});

const {
  createTask: createPortraitTask,
  reset: resetPortraitTask,
  isGenerating: isPortraitGenerating,
} = useSpriteTask({
  mateSprite: editingSprite,
  taskMode: MateSpriteTaskMode.Portrait,
  finish: (mateSprite: MateSprite) => {
    editingSprite.value.portraitMedia = mateSprite.portraitMedia;
    editingSprite.value.portraitMediaId = mateSprite.portraitMedia?.id;
  },
});

const {
  createTask: createFullTask,
  reset: resetFullTask,
  isGenerating: isFullGenerating,
} = useSpriteTask({
  mateSprite: editingSprite,
  taskMode: MateSpriteTaskMode.Full,
  finish: (mateSprite: MateSprite) => {
    initImageSprite({
      animations: mateSprite.animationzz!,
      imageMedia: mateSprite.imageMedia!,
    });

    editingSprite.value.portraitMedia = mateSprite.portraitMedia;
    editingSprite.value.portraitMediaId = mateSprite.portraitMedia?.id;
    editingSprite.value.avatarMedia = mateSprite.avatarMedia;
    editingSprite.value.avatarMediaId = mateSprite.avatarMedia?.id;
  },
});

const canProceedToGeneration = computed(() => selectedImage.value !== null);
const handleGenerate = async () => {
  if (!canProceedToGeneration.value) return;
  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: 8,
      cb: handleGenerate,
    })
  )
    return;
  if (isEdit.value) {
    try {
      handleMateSpriteTask(editingSprite.value, "all", async (sprite) => {
        if (sprite.id !== editingSprite.value.id) {
          // 创建了新的了
          editingSprite.value = sprite;
          spriteId.value = sprite.id!;
        }
        editingSprite.value.prompt = undefined;
        createFullTask({
          spriteId: spriteId.value,
          referenceMediaId: selectedImage.value?.mediaId,
          prompt: promptDescription.value,
        });
      });
    } catch (e) {
      console.error(e);
    }
    return;
  }
  if (!selectedImage.value)
    return showFailToast(t("sprite.pleaseSelectReferenceImage"));

  try {
    editingSprite.value.prompt = undefined;
    await startCreateMateTask();
    spriteId.value = editingSprite.value.id!;
    currentStep.value = 3;
  } catch (error) {
    console.error("Generation failed:", error);
  }
};

const initSort = () => {
  editingSprite.value.animationzz?.sort((a, b) => {
    // 按照 下右左上的顺序排列
    const order = [
      MateSpriteDirection.Up,
      MateSpriteDirection.Left,
      MateSpriteDirection.Down,
      MateSpriteDirection.Right,
    ];
    return order.indexOf(a.direction) - order.indexOf(b.direction);
  });
};

const initImageSprite = (data: {
  animations: MateSpriteAnimation[];
  imageMedia: Media;
}) => {
  const { animations, imageMedia } = data;

  let directionMap = new Map<MateSpriteDirection, MateSpriteAnimation>();
  animations.forEach((animation) => {
    directionMap.set(animation.direction, animation);
  });

  editingSprite.value.animationzz?.forEach((v) => {
    const animation = directionMap.get(v.direction);
    if (animation) {
      v.media = animation.media;

      if (v.direction === MateSpriteDirection.Down) {
        editingSprite.value.imageMedia = imageMedia;
        editingSprite.value.imageMediaId = imageMedia.id!;
      }
    }
  });
  editingSprite.value.prompt = undefined;
  initSort();
};

const showMediaPreviewPopup = ref(false);
const activeAnimationIndex = ref(0);

const resetState = () => {
  currentStep.value = 1;
  editingSprite.value = {} as MateSprite;

  resetFrameCreate();
  resetFrameTask();
  resetAvatarTask();
  resetPortraitTask();
  resetFullTask();
};

const handlePortraitRegen = async (
  mode: "imageMediaId" | "referenceMediaId" | "text",
  overwrite: boolean,
  prompt?: string
) => {
  if (!overwrite) {
    const loading = showLoadingToast(t("sprite.creatingNewSprite"));
    const newSprite = await cloneMateSprite(editingSprite.value);
    loading.close();
    editingSprite.value = newSprite;
    spriteId.value = newSprite.id!;
  }
  if (mode === "imageMediaId") {
    createPortraitTask({
      spriteId: spriteId.value!,
      imageMediaId: editingSprite.value.imageMedia?.id,
      method: "pixel",
      prompt,
    });
  } else if (mode === "referenceMediaId") {
    createPortraitTask({
      spriteId: spriteId.value!,
      referenceMediaId: editingSprite.value.referenceMediaId,
      method: "reference",
      prompt,
    });
  } else if (mode === "text") {
    createPortraitTask({
      spriteId: spriteId.value!,
      prompt,
      method: "text",
    });
  }
};

const handleAvatarRegen = async (
  mode: "imageMediaId" | "referenceMediaId" | "text",
  overwrite: boolean,
  prompt?: string
) => {
  if (!overwrite) {
    const loading = showLoadingToast(t("sprite.creatingNewSprite"));
    const newSprite = await cloneMateSprite(editingSprite.value);
    loading.close();
    editingSprite.value = newSprite;
    spriteId.value = newSprite.id!;
  }
  if (mode === "imageMediaId") {
    createAvatarTask({
      spriteId: spriteId.value!,
      imageMediaId: editingSprite.value.imageMedia?.id,
      method: "pixel",
      prompt,
    });
  } else if (mode === "referenceMediaId") {
    createAvatarTask({
      spriteId: spriteId.value!,
      referenceMediaId: editingSprite.value.referenceMediaId,
      method: "reference",
      prompt,
    });
  } else if (mode === "text") {
    createAvatarTask({
      spriteId: spriteId.value!,
      prompt,
      method: "text",
    });
  }
};

const clickUpload = async (type: "portrait" | "avatar" | "reference") => {
  if (type === "portrait" || type === "avatar")
    if (!isEdit.value)
      return showFailToast(t("sprite.pleaseCompleteCreationBeforeEditing"));

  mediaPreviewType.value = type;
  showMediaSelectPopup.value = true;
};

const clickUploadOrRegen = async (type: "portrait" | "avatar") => {
  const image =
    type === "avatar"
      ? editingSprite.value.avatarMedia
      : editingSprite.value.portraitMedia;

  const list: CustomGlobalActionSheetItem[] = [];

  if (image) {
    list.push();

    modalStore.openCustomGlobalActionSheet({
      title: "",
      list: [
        {
          content: "改图",
          callback: () => {
            sessionStorageSet("mate-wish-edit", {
              referenceImagezz: [image],
            });

            setTimeout(() => {
              router.push({
                name: "mateWishEdit",
              });
            });
          },
        },
        {
          content: type === "avatar" ? "编辑头像" : "编辑立绘",
          callback: () => {
            activeType.value = type;
            showEditAvatarOrPortrait.value = true;
          },
        },
      ],
    });
  } else {
    activeType.value = type;
    showEditAvatarOrPortrait.value = true;
  }
};

const handleEditAvatarOrPortrait = async (
  type: "text" | "reference" | "pixel",
  overwrite: boolean,
  prompt?: string
) => {
  let fn =
    activeType.value === "portrait" ? handlePortraitRegen : handleAvatarRegen;

  if (type === "reference") {
    fn("referenceMediaId", overwrite, prompt);
  } else if (type === "pixel") {
    fn("imageMediaId", overwrite, prompt);
  } else if (type === "text") {
    fn("text", overwrite, prompt);
  }
};

const clickDelete = async (type: "portrait" | "avatar") => {
  try {
    await showConfirmDialog({
      message: t("sprite.confirmDelete", { type: t(`sprite.${type}`) }),
    });
  } catch {
    return;
  }

  if (type === "portrait") {
    const loading = showLoadingToast(t("sprite.deletingPortrait"));
    try {
      await updateMateSprite(editingSprite.value.id!, {
        portraitMediaId: null,
      });
      loading.close();
      editingSprite.value.portraitMediaId = undefined;
      editingSprite.value.portraitMedia = undefined;
      showMediaPreviewModal.value = false;
    } catch (e) {
      loading.close();
    }
  } else if (type === "avatar") {
    const loading = showLoadingToast(t("sprite.deletingAvatar"));
    try {
      await updateMateSprite(editingSprite.value.id!, {
        avatarMediaId: null,
      });
      loading.close();
      editingSprite.value.avatarMediaId = undefined;
      editingSprite.value.avatarMedia = undefined;
      showMediaPreviewModal.value = false;
    } catch (e) {
      loading.close();
    }
  }
};

const clickCreate = async (isNewUser = false) => {
  if (isPreset.value && createMateType.value !== MateType.SelfAvatar) {
    if (!editingSprite.value.id)
      return showFailToast(t("sprite.pleaseSelectPresetSprite"));
    const loading = showLoadingToast(t("sprite.creating"));

    try {
      const { item } = await api.cloneOneMate(
        editingSprite.value.mateId!,
        false
      );
      await mateStore.fetchMateList();
      loading.close();
      showSuccessToast({
        message: t("sprite.createSuccess"),
        onClose: () =>
          router.replace({
            name: "mateEdit",
            params: { uuid: item.uuid },
          }),
      });
    } catch {
      loading.close();
    }
    return;
  }

  if (!name.value && !isPreset.value && !isImage.value)
    return showFailToast(t("sprite.pleaseEnterSpriteName"));

  const noPrompt = !editingSprite.value.prompt;

  if (noPrompt) {
    if (!selectedImage.value && !editingSprite.value.referenceMediaId)
      return showFailToast(t("sprite.pleaseSelectReferenceImage"));

    if (!editingSprite.value.animationzz?.length)
      return showFailToast(t("sprite.pleaseGenerateSprite"));
  } else {
    if (!editingSprite.value)
      return showFailToast(t("sprite.pleaseSelectPresetSprite"));
  }
  const msg = isNewUser
    ? t("sprite.preparingToEnterYourHouse")
    : t("sprite.creating");

  const loading = showLoadingToast(msg);
  try {
    const mate: Mate = isNewUser ? mateStore.userMate : mateStore.currentMate!;
    await getFrameMedia(true, mate.id);

    if (noPrompt) {
      const isImageMode = mode.value === MateSpriteMode.Image;

      let referenceMediaId = selectedImage.value?.mediaId;

      if (!isImageMode) {
        if (userStore.user.id === editingSprite.value.referenceMedia?.userId) {
          referenceMediaId = editingSprite.value.referenceMedia?.id;
        } else {
          referenceMediaId = editingSprite.value.avatarMedia?.id;
        }
      }

      const _mateSprite = await createMateSprite({
        name: name.value,
        mateId: mate.id,
        mode: MateSpriteMode.Image,
        imageMediaId: editingSprite.value!.imageMedia?.id,
        portraitMediaId: editingSprite.value!.portraitMedia?.id,
        avatarMediaId: editingSprite.value!.avatarMedia?.id,

        referenceMediaId,
        sourceSpriteId: isImageMode ? undefined : editingSprite.value.id,
      });
      editingSprite.value.uuid = _mateSprite.item.uuid;
      spriteId.value = _mateSprite.item.id!;
    } else {
      const _mateSprite = await createMateSprite({
        name: name.value,
        mateId: mate.id,
        mode: MateSpriteMode.Compose,
        imageMediaId: editingSprite.value!.imageMedia?.id,
        portraitMediaId: editingSprite.value!.portraitMedia?.id,
        avatarMediaId: editingSprite.value!.avatarMedia?.id,

        frameMediaId: editingSprite.value!.frameMedia?.id,
        prompt: editingSprite.value!.prompt,
      });
      editingSprite.value.uuid = _mateSprite.item.uuid;
      spriteId.value = _mateSprite.item.id!;
    }
    await afterCreate();

    if (isNewUser) {
      // 将这个 sprite 添加到小屋
      await houseStore.getOrFetchHouseSession(
        userStore.user.currentMateHouseSession.uuid
      );
      await houseStore.updateHouseSession([spriteId.value!]);
      await checkMateSpriteHasAnimationzz(editingSprite.value.uuid!);
      await houseStore.getOrFetchHouseSession(
        houseStore.houseSession!.uuid,
        true
      );
      await houseStore.initFullListIfAbsent();

      router.replace({
        name: "dreamHome",
        query: { showTutorial: isNewUser ? "true" : undefined },
      });
    } else {
      await checkMateSpriteHasAnimationzz(editingSprite.value.uuid!);

      showSuccessToast({
        message: t("sprite.createSuccess"),
        onClose: () => {
          if (
            [MateType.User, MateType.SelfAvatar].includes(createMateType.value!)
          ) {
            router.replace({
              name: "treeholeSelfSetting",
              query: {
                mateUuid: mate.uuid,
              },
            });
          } else {
            routerBack();
          }
        },
      });
    }
    loading.close();
  } catch (e) {
    console.log(e);
    loading.close();
  }
};

const getFrameMedia = async (isCreate = false, mateId: number) => {
  const { portraitMedia, avatarMedia } = editingSprite.value!;

  if (
    portraitMedia?.userId !== userStore.user.id &&
    editingSprite.value.portraitMedia
  ) {
    // 下载头像图片并转换为 File 格式
    const item = await uploadMediaFromUrl(
      editingSprite.value!.portraitMedia.url!,
      mateId
    );

    editingSprite.value.portraitMedia = item;
    editingSprite.value.portraitMediaId = item.id;

    if (isCreate)
      api.createOneMateMedia({
        mediaId: item.id,
        type: 1,
      });
  }

  if (
    avatarMedia?.userId !== userStore.user.id &&
    editingSprite.value.avatarMedia
  ) {
    const item = await uploadMediaFromUrl(
      editingSprite.value!.avatarMedia.url!,
      mateId
    );

    editingSprite.value.avatarMedia = item;
    editingSprite.value.avatarMediaId = item.id;
  }
};

const checkMateSpriteHasAnimationzz = async (
  uuid: string
): Promise<boolean> => {
  // 使用循环轮询而不是递归
  while (true) {
    await new Promise((r) => setTimeout(r, 2000));

    try {
      const res = await readMateSprite(uuid);

      // 更新编辑中的精灵数据
      editingSprite.value = res.item;

      // 如果有动画数据，返回 true
      if (res.item.animationzz?.length && res.item.animationzz[0].media?.url) {
        return true;
      }
    } catch (error) {
      console.error("轮询精灵状态时出错:", error);
    }
  }
};

const afterCreate = async () => {
  await updateMateInfo();
  await Promise.all([
    dungeonStore.fetchMateSpriteList(mateStore.currentMate!.id!, true),
    mateStore.fetchMateList(false),
  ]);

  return mateStore.list.length === 1 ? 1 : undefined;
};

const updateMateInfo = async () => {
  const mate = mateStore.currentMate;
  if (mate) {
    if (!mate.mediaUrl) {
      await mateStore.updateMateDetail(mate.uuid, {
        name: name.value,
        mediaUrl: editingSprite.value.avatarMedia?.url,
        mediaId: editingSprite.value.avatarMedia?.id,
      });
    }
  }
};

const clickLogin = () => {
  if (!name.value) return showFailToast(t("sprite.pleaseEnterSpriteName"));
  if (!editingSprite.value)
    return showFailToast(t("sprite.pleaseSelectPresetSprite"));
  userStore.registerCallback = () => {
    clickCreate(true);
  };
  userStore.openLoginModal("register");
};

const isLoadingNextPage = ref(false);
const handleScroll = (e: Event) => {
  if (isLoadingNextPage.value || isLoaded.value) return;

  const target = e.target as HTMLElement;
  const scrollLeft = target.scrollLeft;
  const scrollWidth = target.scrollWidth;
  const clientWidth = target.clientWidth;

  if (scrollLeft >= scrollWidth - clientWidth - 100) {
    isLoadingNextPage.value = true;
    loadNextPage();
  }
};

const clickOpenMediaPreviewModal = (
  url: string,
  type: "portrait" | "avatar"
) => {
  mediaPreviewUrl.value = url;
  mediaPreviewType.value = type;
  showMediaPreviewModal.value = true;
};

const showCustomSpriteEditor = ref(false);
const clickFrame = () => {
  if (!isEdit.value) {
    showFailToast(t("sprite.pleaseCompleteCreationBeforeEditing"));
    return;
  }
  if (
    editingSprite.value.animationzz?.length &&
    editingSprite.value.animationzz[0].media?.url
  ) {
    // 编辑模式
    if (editingSprite.value.mode === MateSpriteMode.Compose) {
      showSpriteEditor.value = true;
    } else {
      showCustomSpriteEditor.value = true;
    }
    return;
  }

  const list: CustomGlobalActionSheetItem[] = [];

  if (selectedImage.value?.mediaId) {
    list.push({
      content: t("sprite.generateUsingReferenceImage"),
      callback() {
        editingSprite.value.prompt = undefined;
        createTask({
          spriteId: spriteId.value,
          referenceMediaId: selectedImage.value?.mediaId,
        });
      },
      paymentInfo: {
        type: "power",
        amount: 4,
      },
    });
  }

  if (editingSprite.value.portraitMedia?.id) {
    list.push({
      content: t("sprite.generateUsingPortraitImage"),
      callback() {
        editingSprite.value.prompt = undefined;
        createTask({
          spriteId: spriteId.value,
          referenceMediaId: editingSprite.value.portraitMedia?.id,
        });
      },
      paymentInfo: {
        type: "power",
        amount: 4,
      },
    });
  }

  if (editingSprite.value.avatarMedia?.id) {
    list.push({
      content: t("sprite.generateUsingAvatarImage"),
      callback() {
        editingSprite.value.prompt = undefined;
        createTask({
          spriteId: spriteId.value,
          referenceMediaId: editingSprite.value.avatarMedia?.id,
        });
      },
      paymentInfo: {
        type: "power",
        amount: 4,
      },
    });
  }

  list.push({
    content: t("sprite.generateFromEditor"),
    callback() {
      showSpriteEditor.value = true;
    },
  });

  if (
    editingSprite.value.animationzz?.length &&
    editingSprite.value.animationzz[0].media?.url
  ) {
    list.push({
      content: t("sprite.editSingleFrame"),
      callback() {
        showCustomSpriteEditor.value = true;
      },
    });
  }

  modalStore.openCustomGlobalActionSheet({
    title: t("sprite.editPixelSprite"),
    list,
  });
};

const openFramePopup = () => {
  if (
    !selectedImage.value?.mediaId &&
    !editingSprite.value.portraitMedia?.id &&
    !editingSprite.value.avatarMedia?.id
  )
    return showFailToast(`请先上传参考图|头像|立绘`);

  const list: CustomGlobalActionSheetItem[] = [];

  if (selectedImage.value?.mediaId) {
    list.push({
      content: `重新生成像素小人`,
      callback() {
        editingSprite.value.prompt = undefined;
        showCustomSpriteEditor.value = false;
        createTask({
          spriteId: spriteId.value,
          referenceMediaId: selectedImage.value?.mediaId,
        });
      },
      paymentInfo: {
        type: "power",
        amount: 4,
      },
    });
  }

  if (editingSprite.value.portraitMedia?.id) {
    list.push({
      content: t("sprite.generateUsingPortraitImage"),
      callback() {
        editingSprite.value.prompt = undefined;
        showCustomSpriteEditor.value = false;
        createTask({
          spriteId: spriteId.value,
          referenceMediaId: editingSprite.value.portraitMedia?.id,
        });
      },
      paymentInfo: {
        type: "power",
        amount: 4,
      },
    });
  }

  if (editingSprite.value.avatarMedia?.id) {
    list.push({
      content: t("sprite.generateUsingAvatarImage"),
      callback() {
        editingSprite.value.prompt = undefined;
        showCustomSpriteEditor.value = false;
        createTask({
          spriteId: spriteId.value,
          referenceMediaId: editingSprite.value.avatarMedia?.id,
        });
      },
      paymentInfo: {
        type: "power",
        amount: 4,
      },
    });
  }

  modalStore.openCustomGlobalActionSheet({
    title: t("sprite.editPixelSprite"),
    list,
  });
};

const openSingleFramePopup = () => {
  showSpriteEditor.value = false;
  showCustomSpriteEditor.value = true;
};
const deleteSprite = async () => {
  showConfirmDialog({
    title: t("characterGenerator.messages.sureDeleteSprite"),
    confirmButtonText: t("Buttons.delete"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      await deleteMateSprite(editingSprite.value!.id!);
      await mateStore.fetchMateList(false);
      routerBack();
    })
    .catch(() => {
      // ignore
    });
};

const initEditSprite = async (force = false) => {
  const spriteId = Number(route.query.spriteId);
  const loading = showLoadingToast(t("Message.loading"));
  if (dungeonStore.mateIdSpriteMap[spriteId] && !force) {
    editingSprite.value = dungeonStore.mateIdSpriteMap[spriteId];
    loading.close();
    return true;
  } else {
    await dungeonStore.fetchMateSpriteList(mateStore.currentMate!.id!, true);
    if (dungeonStore.mateIdSpriteMap[spriteId]) {
      editingSprite.value = dungeonStore.mateIdSpriteMap[spriteId];
      loading.close();
      return true;
    }
  }

  if (!editingSprite.value) {
    routerBack();
    loading.close();
    return false;
  }

  if (mateStore.currentMate?.id !== editingSprite.value.mateId) {
    await mateStore.changeCurrentMate(editingSprite.value.mateId!);
  }
  loading.close();
  return true;
};

const checkIsGenerating = computed(() => {
  return (
    isFullGenerating.value ||
    isPortraitGenerating.value ||
    isAvatarGenerating.value ||
    isFrameGenerating.value ||
    isAllGenerating.value
  );
});

const checkSpriteStatusIfIsPending = async () => {
  if (!editingSprite.value.uuid) return;
  const res = await readMateSprite(editingSprite.value.uuid!);
  editingSprite.value = res.item;
  if (editingSprite.value.status === 1) {
    await new Promise((r) => setTimeout(r, 3000));
    checkSpriteStatusIfIsPending();
  } else {
    await dungeonStore.fetchMateSpriteList(editingSprite.value.mateId!, true);

    showSuccessToast(t("sprite.spriteGenerationCompleted"));
  }
};

// 创建 mate
const promptDescription = ref("");

onMounted(async () => {
  const mateType = Number(route.query.mateType);
  createMateType.value = mateStore.currentMate?.type;
  if (spriteId.value) {
    if (!(await initEditSprite())) return;

    name.value = editingSprite.value.name!;
    mode.value = editingSprite.value.mode!;

    if (editingSprite.value.referenceMedia) {
      selectedImage.value = {
        mediaId: editingSprite.value.referenceMediaId!,
        url: editingSprite.value.referenceMedia?.url,
      };
      currentStep.value = 3;
      initSort();
    }

    if (editingSprite.value.status === 1) {
      checkSpriteStatusIfIsPending();
    }

    if (editingSprite.value.prompt) {
      initPreviewFromPrompt(editingSprite.value.prompt);
    }
  }

  if (isPreset.value) {
    mode.value = MateSpriteMode.Compose;
    if (!mateType) {
      createMateType.value = MateType.Original;
    }
  }

  if (isImage.value) {
    mode.value = MateSpriteMode.Image;
  }

  if (mateType) {
    createMateType.value = mateType as MateType;
  }

  loadNextPage();
});

onBeforeUnmount(() => {
  resetState();
});
</script>
